import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Platform } from '@angular/cdk/platform';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, fromEvent, map, Observable, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { DOCUMENT, ViewportScroller } from '@angular/common';

import { INotification } from '@alpha/types';
import {
  AssetsRegistrationService,
  CsrfService,
  NotificationService,
} from 'app-service';
import { Icon, INavListItem } from 'app-core';

import IconType = Data.Core.IconType;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  viewProviders: [NotificationService],
})
export class AppComponent implements OnInit, OnDestroy {
  public isOnline: boolean;
  public modalVersion: boolean;
  public modalPwaEvent: any;
  public modalPwaPlatform: string | undefined;
  public navListItems: Array<INavListItem> = [];
  public notifications!: INotification;
  public title = 'alpha';
  private readonly document = inject(DOCUMENT);
  private readonly viewport = inject(ViewportScroller);
  public readonly showScroll$: Observable<boolean> = fromEvent(
    this.document,
    'scroll',
  ).pipe(map(() => this.viewport.getScrollPosition()?.[1] > 0));

  onScrollToTop(): void {
    this.viewport.scrollToPosition([0, 0]);
  }

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  public constructor(
    private readonly _cdRef: ChangeDetectorRef,
    private readonly csrfService: CsrfService,
    private readonly _iconService: AssetsRegistrationService,
    private readonly _notificationService: NotificationService,
    private platform: Platform,
    private readonly _route: Router,
    private swUpdate: SwUpdate,
    private readonly _translocoSvc: TranslocoService,
  ) {
    this.isOnline = false;
    this.modalVersion = false;

    this.csrfService
      .loadToken()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this._getNotification();
        this.updateOnlineStatus();
      });
  }

  public ngOnInit(): void {
    this._iconService.init();
    this._loadLocals();

    window.addEventListener('online', this.updateOnlineStatus.bind(this));
    window.addEventListener('offline', this.updateOnlineStatus.bind(this));

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter(
          (evt: any): evt is VersionReadyEvent => evt.type === 'VERSION_READY',
        ),
        map((evt: any) => {
          console.info(
            `currentVersion=[${evt.currentVersion} | latestVersion=[${evt.latestVersion}]`,
          );
          this.modalVersion = true;
        }),
      );
    }

    this.loadModalPwa();
  }

  public displayHeader(): boolean {
    const routes: Array<string> = [
      '/account/login',
      '/account/sign-up',
      '/account/recover-account',
    ];
    // return !routes.some((route) => route === this._router.url);
    return false;
  }

  private _getNotification(): void {
    this._notificationService
      .getNotifications()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: Array<INotification>) => {
        this.notifications = value[0];
      });
  }

  private _loadLocals(): void {
    this._translocoSvc
      .selectTranslate(['home'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: Array<string>) => {
        this.navListItems = [
          {
            label: value[0],
            link: 'home',
            icon: new Icon({
              icon: IconType.homeIcon,
            }),
          },
        ];
        this._cdRef.markForCheck();
      });
  }

  private updateOnlineStatus(): void {
    this.isOnline = window.navigator.onLine;
  }

  public updateVersion(): void {
    this.modalVersion = false;
    window.location.reload();
  }

  public closeVersion(): void {
    this.modalVersion = false;
  }

  private loadModalPwa(): void {
    if (this.platform.ANDROID) {
      window.addEventListener('beforeinstallprompt', (event: any) => {
        event.preventDefault();
        this.modalPwaEvent = event;
        this.modalPwaPlatform = 'ANDROID';
      });
    }

    if (this.platform.IOS && this.platform.SAFARI) {
      const isInStandaloneMode =
        'standalone' in window.navigator &&
        (<any>window.navigator)['standalone'];
      if (!isInStandaloneMode) {
        this.modalPwaPlatform = 'IOS';
      }
    }
  }

  public addToHomeScreen(): void {
    this.modalPwaEvent.prompt();
    this.modalPwaPlatform = undefined;
  }

  public closePwa(): void {
    this.modalPwaPlatform = undefined;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
